import dialogFormMixin from '@/agilis/components/DialogForm/dialogFormMixin'
import FormList from '@/agilis/components/Form/FormList/FormList.vue'
import jsonLogic from '@/plugins/jsonLogic'
import L from 'lodash'
import Vue from 'vue'
import ResizableContainer from '@/agilis/components/ResizableContainer/ResizableContainer.vue'

import { mapActions, mapGetters } from 'vuex'

const d = require('deepdash').default
d(L)

function sortObjectKeys (obj) {
  const keys = Object.keys(obj)
  keys.sort()
  keys.forEach(key => {
    const value = obj[key]
    delete obj[key]
    return Object.assign(obj, { [key]: value })
  })
  return obj
}

export default {
  name: 'JsonEditor',
  mixins: [dialogFormMixin],
  treeItemLastId: 1,

  components: {
    ResizableContainer
  },

  props: {
    json: Object,
    schema: Object,
    editDisabled: Boolean,
    customValidator: Function
  },

  data () {
    return {
      formCustomComponents: {
        FormList
      },
      tree: [],
      activeTreeItems: [],
      openTreeItems: [],
      innerJson: {},
      currentNodeForm: null,
      currentItemTree: null
    }
  },

  watch: {
    json: {
      handler (newValue) {
        if (newValue) {
          this.innerJson = L.cloneDeep(newValue)
          this.tree = this.createTree()
        }
      },
      immediate: true
    }
  },

  computed: {
    ...mapGetters({
      loading: 'loaderStore/loading',
      userId: 'userDataStore/userId'
    }),

    activeTreeItem () {
      const itemTreeId = this.activeTreeItems[0]
      if (itemTreeId) {
        this.currentItemTree = this.$refs?.tree?.nodes[itemTreeId]?.item
        this.createCurrentNodeForm()
        const itemTree = L.cloneDeep(this.currentItemTree)
        return itemTree || null
      }
      return null
    },

    jsonTitle () {
      return this.innerJson?.name ? this.$t(this.innerJson?.name) : this.rootName
    },

    jsonDescription () {
      return this.innerJson?.description && this.$t(this.innerJson?.description)
    },

    rootName () {
      return this.$t(this.innerJson?.name) ?? this.innerJson?.id ?? this.schema?.id ?? 'root'
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'notifyMessage': 'notificationStore/notifyMessage',
      'notifyUserConfirm': 'notificationStore/notifyUserConfirm',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    // /////////////////////////////////////////////////////////////
    // Tree management utils
    // /////////////////////////////////////////////////////////////
    getNewTreeItemId () {
      return this.$options.treeItemLastId++
    },

    createCurrentNodeForm () {
      const treeItem = this.currentItemTree
      const jsonNode = treeItem?.jsonLink.node

      if (this.currentNodeForm?.treeItemId === treeItem.id) {
        return
      }
      this.currentNodeForm = null
      if (jsonNode) {
        let jsonNodeSchema, model
        if (treeItem.jsonLink.schemaId) {
          jsonNodeSchema = this.getSchemaById(treeItem.jsonLink.schemaId)
          if (jsonNodeSchema.type === 'array') {
            const columns = Object.values(jsonNodeSchema.form?.columns)
            const arrayComponent = columns.find(value => value.type === 'datatable' || value.type === 'FormList')
            model = {
              [arrayComponent.id]: L.clone(jsonNode)
            }
          } else {
            model = L.clone(jsonNode)
          }
        } else {
          return
        }

        if (!this.hasSchemaForm(jsonNodeSchema)) {
          return
        }

        const { columns, formRows, jsonNodeKey } = this.getSchemaForm(jsonNodeSchema)

        this.checkJsonNodeConsistency(model, jsonNodeSchema)
        if (jsonNodeKey && !model[jsonNodeKey]) {
          model[jsonNodeKey] = treeItem.name
        }

        this.currentNodeForm = { model: model, fields: columns, formRows, treeItemId: treeItem.id }
      }
    },

    createTree () {
      const rootTreeItem = this.createTreeItem(this.rootName, null, this.schema.id, this.innerJson)
      this.createSubTree(rootTreeItem)
      return [rootTreeItem]
    },

    createSubTree (treeItem) {
      if (treeItem.jsonLink.node) {
        for (let key of Object.keys(treeItem.jsonLink.node)) {
          this.addTreeNode(key, treeItem)
        }
      }
      return treeItem
    },

    addTreeNode (jsonKey, treeItemParent) {
      const jsonNodeParent = treeItemParent.jsonLink.node
      const jsonNode = jsonNodeParent[jsonKey]
      if (jsonNode && (typeof jsonNode === 'object')) {
        let newTreeItemName = jsonKey
        let childNodeSchema = null
        let isNodeInForm = false
        if (treeItemParent.jsonLink.schemaId) {
          const parentNodeSchema = this.getSchemaById(treeItemParent.jsonLink.schemaId)
          isNodeInForm = this.isNodeInForm(jsonKey, parentNodeSchema)
          let childSchemaId = parentNodeSchema.properties?.[jsonKey]?._ref
          if (!childSchemaId && !parentNodeSchema.properties?.[jsonKey] && parentNodeSchema.patternProperties) {
            let pattern = Object.keys(parentNodeSchema.patternProperties).find(pattern => jsonKey.match(pattern))
            childSchemaId = pattern ? parentNodeSchema.patternProperties[pattern]._ref : null
          }
          if (childSchemaId) {
            childNodeSchema = this.getSchemaById(childSchemaId)
          }
        }

        if (!isNodeInForm) {
          let newTreeItem = this.createTreeItem(newTreeItemName, treeItemParent, childNodeSchema?.id, jsonNode)
          treeItemParent.children.push(newTreeItem)

          if (typeof jsonNode !== 'string') {
            for (let key of Object.keys(jsonNode)) {
              this.addTreeNode(key, newTreeItem)
            }
          }
        }
      }
    },

    createTreeItem (name, parent, jsonNodeSchemaId, jsonNode) {
      return {
        id: this.getNewTreeItemId(),
        name: name,
        children: [],
        parent: parent,
        jsonLink: {
          schemaId: jsonNodeSchemaId,
          node: jsonNode
        }
      }
    },

    sortTreeChildren (treeItem) {
      if (treeItem?.jsonLink?.schemaId) {
        const schema = this.getSchemaById(treeItem.jsonLink.schemaId)
        if (schema?.sortProperties === true) {
          if (treeItem?.children) {
            treeItem.children.sort((a, b) => {
              return a.name.localeCompare(b.name)
            })
          }
          if (treeItem?.jsonLink?.node) {
            treeItem.jsonLink.node = sortObjectKeys(treeItem.jsonLink.node)
          }
        }
      }
    },

    async addJsonNode (treeItem) {
      try {
        const missingProperty = this.getMissingProperties(treeItem)[0]
        const jsonNodeSchema = this.getSchemaById(missingProperty.schemaId)
        const { columns, formRows, jsonNodeKey } = this.getSchemaForm(jsonNodeSchema)
        let model = {}
        if (this.hasSchemaForm(jsonNodeSchema) && jsonNodeSchema.type !== 'array') {
          this.checkJsonNodeConsistency(model, jsonNodeSchema)
          const formModel = await this.openFormDialog(
            model,
            columns,
            formRows
          )
          model = formModel
        } else if (jsonNodeSchema.type === 'array') {
          model = []
        }

        const newJsonNodeKey = jsonNodeKey ? model[jsonNodeKey] || missingProperty.id : missingProperty.id
        if (jsonNodeKey && !L.has(jsonNodeSchema.properties, jsonNodeKey) && L.has(model, jsonNodeKey)) {
          delete model[jsonNodeKey]
        }

        const newJsonNode = model
        const newTreeItemName = newJsonNodeKey
        const newTreeItem = this.createTreeItem(newTreeItemName, treeItem, jsonNodeSchema.id, newJsonNode)
        this.createSubTree(newTreeItem)
        treeItem.children.push(newTreeItem)
        treeItem.jsonLink.node[newJsonNodeKey] = newJsonNode
        this.sortTreeChildren(treeItem)

        this.$emit('jsonChanged', this.innerJson)
      } catch (e) {
        // ...
      }
    },

    copyJsonNode (treeItem) {
      if (treeItem.parent) {
        const jsonNodeParent = treeItem.parent.jsonLink.node
        const jsonNodeKey = treeItem.name
        const jsonNodeSchema = this.getSchemaById(treeItem.jsonLink.schemaId)
        const newJsonNode = L.cloneDeep(treeItem.jsonLink.node)

        let newJsonNodeKey
        let c = 1
        newJsonNodeKey = jsonNodeKey + 'Copy'
        while (Object.keys(jsonNodeParent).find(k => k === newJsonNodeKey)) {
          newJsonNodeKey = jsonNodeKey + 'Copy' + c
          c++
        }
        const newTreeItemName = newJsonNodeKey
        const keyProperty = jsonNodeSchema.form?.key
        if (keyProperty && newJsonNode[keyProperty]) {
          newJsonNode[keyProperty] = newJsonNodeKey
        }

        const newTreeItem = this.createTreeItem(newTreeItemName, treeItem.parent, jsonNodeSchema.id, newJsonNode)
        this.createSubTree(newTreeItem)
        treeItem.parent.children.push(newTreeItem)

        Vue.set(jsonNodeParent, newJsonNodeKey, newJsonNode)
        this.sortTreeChildren(treeItem.parent)

        this.$emit('jsonChanged', this.innerJson)
      }
    },

    deleteJsonNode (treeItem) {
      if (treeItem.parent) {
        const indexToDel = L.findLastIndex(treeItem.parent.children, child => {
          return child.id === treeItem.id
        })
        treeItem.parent.children.splice(indexToDel, 1)
        let jsonNodeParent = treeItem.parent.jsonLink.node
        Vue.delete(jsonNodeParent, treeItem.name)
      }

      this.$emit('jsonChanged', this.innerJson)
    },

    /**
     * Updates a JSON node's data when a change is made to the node.
     * It ensures data remains consistent and orgranized.
     * @param {Object} delta - The new change to be applied to the JSON node.
     * @returns {Object} The original delta object.
     */
    checkJsonNodeBeforeChange (delta) {
      const fieldConfig = this.currentNodeForm.fields[Object.keys(delta)[0]]
      if (fieldConfig?.beforeChange?.method && typeof this[fieldConfig.beforeChange.method] === 'function') {
        this[fieldConfig.beforeChange.method](fieldConfig.beforeChange)
      }

      const treeItem = this.currentItemTree
      const jsonNodeSchema = this.getSchemaById(treeItem.jsonLink.schemaId)
      let model = L.cloneDeep(this.currentNodeForm.model)
      if (fieldConfig.type === 'FormList') {
        if (jsonNodeSchema.type === 'array') {
          model = delta[fieldConfig.id]
        }
      }
      const jsonNodeParent = treeItem.parent?.jsonLink.node
      const keyProperty = jsonNodeSchema.form?.key
      const newJsonNodeKey = model[keyProperty]
      const oldJsonNodeKey = treeItem.name
      if (newJsonNodeKey && oldJsonNodeKey !== newJsonNodeKey) {
        if (jsonNodeParent) {
          delete jsonNodeParent[oldJsonNodeKey]
        }
        treeItem.name = newJsonNodeKey
      }
      if (keyProperty && !jsonNodeSchema.properties[keyProperty] && L.has(model, keyProperty)) {
        delete model[keyProperty]
      }
      if (jsonNodeParent) {
        jsonNodeParent[treeItem.name] = model
      } else {
        this.innerJson = model
      }
      treeItem.jsonLink.node = model
      this.sortTreeChildren(treeItem.parent)

      this.$emit('jsonChanged', this.innerJson)
      return delta
    },

    checkJsonNodeConsistency (jsonNode, jsonNodeSchema) {
      jsonNodeSchema?.properties && Object.entries(jsonNodeSchema.properties).forEach(([propName, propConfig]) => {
        if (!jsonNode.hasOwnProperty(propName)) {
          const initialValue = this.getPropertyInitialValue(propName, propConfig, jsonNodeSchema)
          if (initialValue) {
            L.set(jsonNode, propName, initialValue)
          }
        }
      })
    },

    getPropertyInitialValue (propName, propConfig, jsonNodeSchema) {
      let initialValue
      const defaultValue = jsonNodeSchema?.form?.columns?.[propName]?.defaultValue

      let propertyType = null
      if (propConfig.type) {
        propertyType = propConfig.type
      } else if (propConfig._ref && this.schema[propConfig._ref]) {
        propertyType = this.schema[propConfig._ref].type
      }

      switch (propertyType) {
        case 'array':
          initialValue = L.clone(defaultValue) || []
          break
        case 'object':
          initialValue = L.clone(defaultValue) || {}
          break
        default: initialValue = defaultValue || null
      }
      return initialValue
    },

    getMissingProperties (treeItem) {
      let missingProperties = []
      const jsonNodeSchema = this.getSchemaById(treeItem.jsonLink.schemaId)
      if (jsonNodeSchema?.properties) {
        const complexProperties = Object.keys(jsonNodeSchema.properties).filter((property) => {
          return jsonNodeSchema.properties[property]._ref
        })
        const complexValidProperties = complexProperties?.filter((property) => {
          return !jsonNodeSchema.form?.columns?.[property]
        })
        const complexValidMissingProperties = complexValidProperties?.filter((property) => {
          return !treeItem.jsonLink.node?.[property]
        })
        complexValidMissingProperties.forEach(property => {
          missingProperties.push({
            id: property,
            schemaId: jsonNodeSchema.properties[property]._ref
          })
        })
      }
      if (jsonNodeSchema?.patternProperties) {
        Object.keys(jsonNodeSchema.patternProperties).forEach(property => {
          missingProperties.push({
            id: property,
            schemaId: jsonNodeSchema.patternProperties[property]._ref
          })
        })
      }

      return missingProperties
    },

    checkMissingProperty (treeItem, property) {
      const missingProperties = this.getMissingProperties(treeItem)
      return missingProperties.find(missingProperty => property.match(missingProperty.id))
    },

    // /////////////////////////////////////////////////////////////
    // Schema management utils
    // /////////////////////////////////////////////////////////////
    getSchemaById (schemaId) {
      const s = this.schema?.[schemaId] || null
      return s
    },

    isNodeInForm (jsonKey, jsonNodeSchema) {
      if (jsonNodeSchema?.form?.columns?.[jsonKey]) {
        return true
      } else {
        return false
      }
    },

    hasSchemaForm (jsonNodeSchema) {
      return !!jsonNodeSchema?.form?.columns
    },

    getSchemaForm (jsonNodeSchema) {
      const columns = jsonNodeSchema?.form?.columns || {}
      const formRows = jsonNodeSchema?.form?.formRows || []
      const jsonNodeKey = jsonNodeSchema?.form?.key || null

      Object.values(columns)
        .filter((c) => c.type === 'datatable')
        .forEach(c => {
          const fieldId = c.id
          const childSchemaId = jsonNodeSchema.properties[fieldId].items._ref
          const childSchema = this.getSchemaById(childSchemaId)

          columns[fieldId].columns = childSchema.form.columns
          columns[fieldId].formRows = childSchema.form.formRows
        })

      this.customValidator(columns)

      return {
        columns,
        formRows,
        jsonNodeKey
      }
    },

    // /////////////////////////////////////////////////////////////
    // Permissions utils
    // /////////////////////////////////////////////////////////////
    canAddJsonNode (treeItem) {
      const jsonNodeSchema = this.getSchemaById(treeItem.jsonLink.schemaId)
      const disabledLogic = jsonNodeSchema?.jsonLogic
      const disabled = disabledLogic ? jsonLogic.apply(disabledLogic, treeItem.jsonLink.node) : false

      const missingProperties = this.getMissingProperties(treeItem)
      const enabled = missingProperties.length > 0

      return !this.editDisabled && enabled && !disabled
    },

    canDeleteJsonNode (treeItem) {
      return !this.editDisabled && !!treeItem.parent
    },

    canCopyJsonNode (treeItem) {
      return !this.editDisabled && !!treeItem.parent && this.checkMissingProperty(treeItem.parent, treeItem.name)
    },

    popupWarning (fieldConfig) {
      this.notifyMessage(this.$t(fieldConfig.message))
    }
  }
}
